import { useState, useCallback, useEffect, useMemo } from "preact/hooks";
import { useFilteredArray, useSortedArray } from "hooks";
import { TypedTableColummn } from "./datatable";

export interface TypedTableConfig {
    visibleColumns?: string[];
    sortColumn?: string;
    sortOrder?: "ascending" | "descending";
}

export function getTypedTableConfigurationFromLocalStorage(key: string): TypedTableConfig | undefined {
    const val = localStorage.getItem(key);
    if (val) {
        try {
            return JSON.parse(val);
        }
        catch (e) {
            //
        }
    }
    return undefined;
}

export function saveTypedTableConfigurationToLocalStorage(key: string, config: TypedTableConfig) {
    const tmp = {
        sortOrder: config.sortOrder,
        sortColumn: config.sortColumn,
        visibleColumns: config.visibleColumns
    };
    if (!tmp.sortOrder || !tmp.sortColumn || !tmp.visibleColumns) {
        const prev = getTypedTableConfigurationFromLocalStorage(key);
        tmp.sortOrder = tmp.sortOrder ?? prev?.sortOrder;
        tmp.sortColumn = tmp.sortColumn ?? prev?.sortColumn;
        tmp.visibleColumns = tmp.visibleColumns ?? prev?.visibleColumns;
    }
    const data = JSON.stringify(tmp);
    localStorage.setItem(key, data);
}

export function useTypedTableConfiguration<T>(
    data: T[] | null,
    columns: TypedTableColummn<T>[],
    temporaryHiddenColumns?: TypedTableColummn<T>[],
    initialSortColumn?: TypedTableColummn<T>,
    initialConfig?: TypedTableConfig,
    persistConfig?: (config: TypedTableConfig) => void | Promise<void>
) {
    const [filter, setFilter] = useState("");
    const [sortColumn, setSortColumn] = useState(initialSortColumn ?? columns.find(n => n.name === initialConfig?.sortColumn) ?? columns[0]);
    const [getter, setGetter] = useState(() => getOrBuildGetter(sortColumn));
    const [sortOrder, setSortOrder] = useState<"ascending" | "descending">(initialConfig?.sortOrder ?? "ascending");
    const [configuredVisibleColumns, setConfiguredVisibleColumns] = useState(initialConfig?.visibleColumns ? columns.filter(n => initialConfig.visibleColumns?.includes(n.name)) : columns);

    useEffect(() => {
        const newones = configuredVisibleColumns.map(oldColumn => {
            const updatedColumn = columns.find(n => n.name == oldColumn.name) ?? oldColumn;
            return updatedColumn;
        });
        setConfiguredVisibleColumns(newones);
    }, [columns]);

    // todo: useCallback
    const onColumnClicked = useCallback((column: TypedTableColummn<T>) => {
        if (sortColumn == column) {
            setSortOrder(sortOrder == "ascending" ? "descending" : "ascending");
            persistConfig?.({
                sortOrder: sortOrder == "ascending" ? "descending" : "ascending"
            });
        }
        else {
            setSortOrder("ascending");
            setSortColumn(column);
            persistConfig?.({
                sortColumn: column.name,
                sortOrder: "ascending"
            });
            setGetter(() => getOrBuildGetter(column));
        }
    }, [sortOrder, sortColumn, persistConfig]);

    const onColumnVisibilityChanged = useCallback((column: TypedTableColummn<T>, visible: boolean) => {
        const alreadyVisible = -1 != configuredVisibleColumns.indexOf(column);
        let newConfiguredColumns: TypedTableColummn<T>[] | null = null;
        if (!visible && alreadyVisible) {
            newConfiguredColumns = configuredVisibleColumns.filter(n => n != column);
            setConfiguredVisibleColumns(newConfiguredColumns);
        }
        else if (visible && !alreadyVisible) {
            newConfiguredColumns = columns.filter(n => n == column || -1 != configuredVisibleColumns.indexOf(n));
            setConfiguredVisibleColumns(newConfiguredColumns);
        }
        persistConfig?.({
            visibleColumns: newConfiguredColumns?.map(n => n.name)
        });
    }, [columns, temporaryHiddenColumns, configuredVisibleColumns, persistConfig]);

    const visibleColumns = useMemo(() => (
        temporaryHiddenColumns ? configuredVisibleColumns.filter(n => -1 === temporaryHiddenColumns.indexOf(n)) : configuredVisibleColumns
    ), [columns, configuredVisibleColumns, temporaryHiddenColumns]);

    const sortedData = useSortedArray(data, getter, sortOrder == "ascending");

    const lowerFilter = filter.toLowerCase();
    const filterCallback = useCallback((row: T, index: number) => -1 != buildRowContent(row, index, visibleColumns).toLowerCase().indexOf(lowerFilter), [visibleColumns, lowerFilter]);
    const visibleData = useFilteredArray(sortedData, filterCallback);

    return { visibleData, visibleColumns, configuredVisibleColumns, sortColumn, sortOrder, filter, onColumnClicked, onColumnVisibilityChanged, onFilterChanged: setFilter };
}

export function useSelectedRows<TItem, TKey>(getItemKey: (item: TItem) => TKey, allRows?: TItem[]) {
    const [selectedRows, setSelectedRows] = useState<TItem[]>([]);
    const [selectedIds, setSelectedIds] = useState<TKey[]>([]);

    const onRowClicked = useCallback((row: TItem, selected: boolean, index: number) => {
        const alreadySelected = -1 != selectedRows.indexOf(row);
        if (!selected && alreadySelected) {
            const newRows = selectedRows.filter(n => n != row);
            setSelectedRows(newRows);
            setSelectedIds(newRows.map(getItemKey))
        }
        else if (selected && !alreadySelected) {
            const newRows = [...selectedRows, row]
            setSelectedRows(newRows);
            setSelectedIds(newRows.map(getItemKey))
        }
    }, [selectedRows]);

    const selectAll = useCallback(() => {
        if (allRows) {
            setSelectedRows(allRows);
            setSelectedIds(allRows.map(getItemKey))
        }
    }, [allRows])

    const clearAll = useCallback(() => {
        setSelectedRows([]);
        setSelectedIds([])
    }, [allRows])

    const setSelection = useCallback((ids: TKey[], items?: TItem[]) => {
        if (items ?? allRows) {
            setSelectedRows((items ?? allRows)!.filter(n => ids.some(nn => nn == getItemKey(n))));
            setSelectedIds(ids)
        }
    }, [allRows])

    const toggleAll = useCallback(() => {
        if (allRows) {
            const lookup: Record<string, boolean> = {};
            for (var indx in selectedIds)
                lookup["" + selectedIds[indx]] = true;
            let newSelectedRows = allRows.filter(n => !lookup["" + getItemKey(n)]);
            setSelectedRows(newSelectedRows);
            setSelectedIds(newSelectedRows.map(getItemKey))
        }
    }, [allRows, selectedIds])

    const toggleRows = useCallback((rows: TItem[]) => {
        if (rows && allRows) {
            console.log("toggling", [...rows]);
            const lookupToToggle: Record<string, boolean> = {};
            for (var row of rows)
                lookupToToggle["" + getItemKey(row)] = true;

            const lookupExistingSelected: Record<string, boolean> = {};
            for (var id of selectedIds)
                lookupExistingSelected["" + id] = true;

            let newSelectedRows = allRows.filter(n => {
                const key = getItemKey(n);
                const shouldToggle = !!lookupToToggle["" + key];
                const isAlreadySelected = !!lookupExistingSelected["" + key];
                return shouldToggle ? !isAlreadySelected : isAlreadySelected;
            });

            setSelectedRows(newSelectedRows);
            setSelectedIds(newSelectedRows.map(getItemKey))
        }
    }, [allRows, selectedIds])

    useEffect(() => {
        if (allRows) {
            const map = new Map(allRows.map(n => [getItemKey(n), n]));
            const newSelectedRows = selectedRows.filter(n => map.has(getItemKey(n)))
            if (newSelectedRows.length !== selectedRows.length) {
                setSelectedRows(newSelectedRows);
                setSelectedIds(newSelectedRows.map(getItemKey))
            }
        }
    }, [selectedRows, allRows]);

    return { selectedRows, selectedIds, onRowClicked, selectAll, setSelection, clearAll, toggleAll, toggleRows };
}

function getOrBuildGetter<T>(column: TypedTableColummn<T> | null) { // todo: generic constraint on T so as to avoid the type warning below
    if (column?.getSortingKey) {
        return column.getSortingKey;
    }
    if (column?.key) {
        const key = column.key.toString() as keyof T; // tricky
        return (item: T) => (item[key] as unknown as string);
    }
    if (column?.getter) {
        return column.getter as (() => string);
    }
    return null;
}

const rowContentSeparator = "_|_";
function buildRowContent<T>(row: T, rowIndex: number, columns: TypedTableColummn<T>[]) { // todo: generic constraint on T so as to avoid the type warning below
    let result = "";
    if (row && columns) {
        for (const index in columns) {
            const column = columns[index];
            if (!result) result += rowContentSeparator;

            if (column?.getSortingKey) {
                result += column.getSortingKey(row);
            }
            else if (column?.key) {
                const key = column.key.toString() as keyof T; // tricky
                result += row[key];
            }
            else if (column?.getter) {
                result += column.getter(row, rowIndex, column, index as any as number) ?? "";
            }
        }
    }
    return result;
}

